import {
  CF_ALL_ACCESS_MONTHLY_PRICE_USD,
  CF_ALL_ACCESS_MONTHLY_PRICE_OTHER,
  CF_ALL_ACCESS_YEARLY_PRICE,
  CF_ALL_ACCESS_YEARLY_PROMO_PRICE,
  CF_ALL_ACCESS_YEARLY_MONTHLY_SPLIT_PRICE,
  CF_ALL_ACCESS_YEARLY_PROMO_MONTHLY_SPLIT_PRICE,
  CF_ALL_ACCESS_YEARLY_MONTHLY_FULL_PRICE,
  VAT_INCLUDED_CF_ALL_ACCESS_MONTHLY_PRICE_NL,
  VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_MONTHLY_SPLIT_PRICE_NL,
  VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PRICE_NL,
  VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PROMO_PRICE_NL,
  VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PROMO_MONTHLY_SPLIT_PRICE_NL,
  VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_MONTHLY_FULL_PRICE_NL,
  CF_COINS_TOP_UP_25K_PRICE_USD,
  CF_COINS_TOP_UP_100K_PRICE_USD,
  CF_COINS_TOP_UP_250K_PRICE_USD,
  CF_COINS_TOP_UP_1000K_PRICE_USD,
  VAT_INCLUDED_CF_COINS_TOP_UP_25K_PRICE_USD,
  VAT_INCLUDED_CF_COINS_TOP_UP_100K_PRICE_USD,
  VAT_INCLUDED_CF_COINS_TOP_UP_250K_PRICE_USD,
  VAT_INCLUDED_CF_COINS_TOP_UP_1000K_PRICE_USD,
  CF_STUDIO_AI_YEARLY_PROMO_PRICE,
  CF_STUDIO_AI_YEARLY_PROMO_MONTHLY_SPLIT_PRICE,
  VAT_INCLUDED_CF_STUDIO_AI_YEARLY_PROMO_PRICE_NL,
  VAT_INCLUDED_CF_STUDIO_AI_YEARLY_PROMO_MONTHLY_SPLIT_PRICE_NL,
} from 'constants/prices'
import type { CountryCode, Currency } from 'codegen/types'
import { CurrencyCode } from 'codegen/types'
import * as React from 'react'
import { useCurrentCurrency } from './use-current-currency'

import { useUserLocation } from 'hooks/use-user-location'

const ForcedCountryContext = React.createContext<CountryCode | undefined>(undefined)

export function ForcedCountryProvider({
  country,
  children,
}: {
  country: CountryCode | undefined
  children: React.ReactNode
}) {
  return React.createElement(ForcedCountryContext.Provider, { value: country }, children)
}

function decidePrices(country: CountryCode | undefined, currency: Currency) {
  switch (country) {
    case 'NL': {
      return {
        currency: currency,
        prices: {
          monthly: VAT_INCLUDED_CF_ALL_ACCESS_MONTHLY_PRICE_NL,
          yearly: VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PRICE_NL,
          yearlyPromo: VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PROMO_PRICE_NL,
          yearlyMonthlySplit: VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_MONTHLY_SPLIT_PRICE_NL,
          yearlyPromoMonthlySplit: VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PROMO_MONTHLY_SPLIT_PRICE_NL,
          yearlyPromoMonthlyFull: VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_MONTHLY_FULL_PRICE_NL,
          studioMonthly: VAT_INCLUDED_CF_STUDIO_AI_YEARLY_PROMO_MONTHLY_SPLIT_PRICE_NL,
          studioYearly: VAT_INCLUDED_CF_STUDIO_AI_YEARLY_PROMO_PRICE_NL,
          coinsTopUp25k: VAT_INCLUDED_CF_COINS_TOP_UP_25K_PRICE_USD,
          coinsTopUp100k: VAT_INCLUDED_CF_COINS_TOP_UP_100K_PRICE_USD,
          coinsTopUp250k: VAT_INCLUDED_CF_COINS_TOP_UP_250K_PRICE_USD,
          coinsTopUp1000k: VAT_INCLUDED_CF_COINS_TOP_UP_1000K_PRICE_USD,
        },
      }
    }
    default: {
      return {
        currency: currency,
        prices: {
          monthly:
            currency.code === CurrencyCode.Usd
              ? CF_ALL_ACCESS_MONTHLY_PRICE_USD
              : CF_ALL_ACCESS_MONTHLY_PRICE_OTHER,
          yearly: CF_ALL_ACCESS_YEARLY_PRICE,
          yearlyPromo: CF_ALL_ACCESS_YEARLY_PROMO_PRICE,
          yearlyMonthlySplit: CF_ALL_ACCESS_YEARLY_MONTHLY_SPLIT_PRICE,
          yearlyPromoMonthlySplit: CF_ALL_ACCESS_YEARLY_PROMO_MONTHLY_SPLIT_PRICE,
          yearlyPromoMonthlyFull: CF_ALL_ACCESS_YEARLY_MONTHLY_FULL_PRICE,
          studioMonthly: CF_STUDIO_AI_YEARLY_PROMO_MONTHLY_SPLIT_PRICE,
          studioYearly: CF_STUDIO_AI_YEARLY_PROMO_PRICE,
          coinsTopUp25k: CF_COINS_TOP_UP_25K_PRICE_USD,
          coinsTopUp100k: CF_COINS_TOP_UP_100K_PRICE_USD,
          coinsTopUp250k: CF_COINS_TOP_UP_250K_PRICE_USD,
          coinsTopUp1000k: CF_COINS_TOP_UP_1000K_PRICE_USD,
        },
      }
    }
  }
}

const shouldPricesIncludeVat = (country: CountryCode | undefined) => {
  return country === 'NL'
}

export const usePricesForUser = (forceCountry?: CountryCode) => {
  const { country } = useUserLocation()
  const contextCountry = React.useContext(ForcedCountryContext)
  const defaultCurrency = useCurrentCurrency()

  const effectiveCountry = forceCountry ?? contextCountry ?? country
  const prices = decidePrices(effectiveCountry, defaultCurrency)

  return {
    ...prices,
    country,
    pricesIncludeVat: shouldPricesIncludeVat(effectiveCountry),
  }
}
